import React, { memo, useMemo } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { UserMessageButtons } from "./userMessageButtons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { type Userdata } from "@/types/user";
import { createBase64Image, defaultUserAvatar } from "@/helpers/images";
import { StreamMessage } from "../types";

type UserMessageProps = {
  message: StreamMessage;
  userData: Userdata | undefined;
  onCopyMessage: (messageId: string, content: string) => Promise<void>;
  copiedMessageId: string | null;
};

export const UserMessage: React.FC<UserMessageProps> = memo(({ message, userData, onCopyMessage, copiedMessageId }) => {
  const formattedTime = useMemo(() => format(new Date(), "h:m a"), []);

  if (!message) {
    return null;
  }

  return (
    <motion.div
      className="my-4 flex justify-end"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ type: "spring", stiffness: 500, damping: 30, mass: 1 }}
    >
      <div className="flex w-full max-w-[95%] flex-col">
        <motion.div
          className="relative rounded-lg border border-gray-200 bg-user-message p-4 shadow-md"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          <div className="mb-3 flex items-center border-b border-gray-100 pb-2">
            <Avatar className="mr-2 h-6 w-6">
              <AvatarImage
                src={userData?.avatar ? createBase64Image(userData.avatar, "png") : defaultUserAvatar}
                alt={userData?.username}
              />
              <AvatarFallback>{userData?.username?.[0]?.toUpperCase() || "U"}</AvatarFallback>
            </Avatar>
            <span className="mr-2 text-sm font-semibold">{userData?.username || "User"}</span>
            <span className="ml-auto text-xs text-gray-500">{formattedTime}</span>
          </div>
          <div className="pt-1">
            <MarkdownRenderer content={message.content} evidences={message.evidences} processCitations={true} />
          </div>
        </motion.div>
        <UserMessageButtons message={message} onCopyMessage={onCopyMessage} copiedMessageId={copiedMessageId} />
      </div>
    </motion.div>
  );
});
